import _ from 'lodash';
import { computed, getCurrentInstance, Ref } from 'vue-demi';
import { storeToRefs, Store } from 'pinia';
import { useGlobalVars } from './use-global-vars';

interface Global {
  $CommonAuthRefs: AuthRefs;
  $ORAuthStore: Store;
}

/**
 * Depending on environment either use Auth UI store or Action Desk store.
 * Provide a common interface to access auth properties.
 * Auth UI store has to be initialized before using values!
 */
export const useAuth = () => {
  const $global = useGlobalVars().getVueGlobals() as Global;

  if (!_.isEmpty($global.$CommonAuthRefs)) {
    return $global.$CommonAuthRefs;
  }

  const vm = getCurrentInstance();
  const $pinia = vm?.proxy.$pinia;

  if ($pinia && $pinia.state.value['auth']) {
    /* These properties are provided by Action Desk store */
    $global.$CommonAuthRefs = {
      isAuthorized: computed(() => $pinia.state.value['auth'].authorized || false),
      areCookiesExpired: computed(() => $pinia.state.value['auth'].cookiesExpired || false),
      accountId: computed(() => $pinia.state.value['auth'].accountId || ''),
      userId: computed(() => $pinia.state.value['auth'].userId || ''),
      multiUserId: computed(() => $pinia.state.value['auth'].multiUserId || ''),
      username: computed(() => $pinia.state.value['auth'].username || ''),
      tokenExpireTime: computed(() => $pinia.state.value['auth'].tokenExpireTime || 0),
      getToken: () => {
        if ($pinia.state.value['auth'].cookiesExpired) {
          return '';
        }
        return $pinia.state.value['auth'].token || '';
      },
    };
  } else {
    /* These properties are provided by Auth UI store */
    const store: any = storeToRefs<any>($global.$ORAuthStore);
    $global.$CommonAuthRefs = {
      isAuthorized: computed(() => store.authorized.value || false),
      areCookiesExpired: computed(() => store.cookiesExpired.value || false),
      accountId: computed(() => store.accountId.value || ''),
      username: computed(() => store.username.value || ''),
      userId: computed(() => store.userId.value || ''),
      multiUserId: computed(() => store.multiUserId.value || ''),
      tokenExpireTime: computed(() => store.tokenExpireTime.value || 0),
      getToken: () => {
        if (store.cookiesExpired.value) {
          return '';
        }
        return store.token.value || '';
      },
    };
  }

  return $global.$CommonAuthRefs;
};

export interface AuthRefs {
  readonly userId: Ref<string>;
  readonly multiUserId: Ref<string>;
  readonly isAuthorized: Ref<boolean>;
  readonly username: Ref<string>;
  readonly accountId: Ref<string>;
  readonly areCookiesExpired: Ref<boolean>;
  readonly tokenExpireTime: Ref<number>;
  readonly getToken: () => string;
}
